/**
 * Theme Severity Color Utilities
 *
 * Utility functions for mapping severity levels to theme palette colors.
 * Used by Insights, Governance, and Validation components.
 */

/**
 * Get status color based on severity
 * @param {object} theme - MUI theme object
 * @param {string} severity - Severity level ('critical', 'high', 'medium', 'low')
 * @returns {string} Theme color value
 */
export const getSeverityColor = (theme, severity) => {
  if (!theme) return '#8E8E93';

  switch (severity) {
    case 'critical':
      return theme.palette.error.main;
    case 'high':
      return theme.palette.success.main;
    case 'medium':
      return theme.palette.secondary.main;
    case 'low':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.secondary;
  }
};

/**
 * Get status background color based on severity
 * @param {object} theme - MUI theme object
 * @param {string} severity - Severity level
 * @returns {string} Theme color value
 */
export const getSeverityBackgroundColor = (theme, severity) => {
  if (!theme) return '#E3F2FD';

  switch (severity) {
    case 'critical':
      return theme.palette.error.light || '#FFEBEE';
    case 'high':
      return theme.palette.success.light || '#E8F5E9';
    case 'medium':
      return theme.palette.secondary.light || '#DBEAFE';
    case 'low':
      return theme.palette.warning.light || '#FFF3E0';
    default:
      return theme.palette.info.light || '#E3F2FD';
  }
};

/**
 * Get status text color based on severity
 * @param {object} theme - MUI theme object
 * @param {string} severity - Severity level
 * @returns {string} Theme color value
 */
export const getSeverityTextColor = (theme, severity) => {
  if (!theme) return '#1565C0';

  switch (severity) {
    case 'critical':
      return theme.palette.error.dark || '#C62828';
    case 'high':
      return theme.palette.success.dark || '#1A9440';
    case 'medium':
      return theme.palette.secondary.dark || '#1D4ED8';
    case 'low':
      return theme.palette.warning.dark || '#E65100';
    default:
      return theme.palette.info.main || '#1565C0';
  }
};

const colorMapper = {
  getSeverityColor,
  getSeverityBackgroundColor,
  getSeverityTextColor,
};

export default colorMapper;
